// src/pages/veci/CarritoVeci.js
//
// Carrito del veci: los items siguen en el AppContext local (los agrega
// el detalle del negocio). Al enviar, hace POST /pedidos al backend real.

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Button, IconButton, Divider,
  TextField, Chip, Alert, MenuItem, Radio, RadioGroup, FormControlLabel,
  FormLabel, FormControl, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RemoveIcon         from '@mui/icons-material/Remove';
import AddIcon            from '@mui/icons-material/Add';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import SendIcon           from '@mui/icons-material/Send';
import StorefrontIcon     from '@mui/icons-material/Storefront';

import { useApp } from '../../context/AppContext';
import { negocios as apiNegocios } from '../../api/negociosApi';
import { pedidos as apiPedidos } from '../../api/pedidosApi';
import { formatoCOP } from '../../utils/format';

const METODOS_PAGO = [
  { v: 'recibir',          label: 'Pagar al recibir',           descripcion: 'Le pagas al domiciliario cuando llegue.' },
  { v: 'qr',               label: 'Pagar por QR',                descripcion: 'Escaneas el QR del negocio y adjuntas comprobante.' },
  { v: 'fiado_solicitud',  label: 'Pedir fiado',                 descripcion: 'El negocio decide si te fía. Solo si el negocio acepta fiado.' },
];

export default function CarritoVeci() {
  const {
    carrito, cambiarCantidadCarrito, quitarDelCarrito, vaciarCarrito, totalCarrito,
  } = useApp();
  const navigate = useNavigate();

  const [negocio,      setNegocio]      = useState(null);
  const [cargandoNeg,  setCargandoNeg]  = useState(false);
  const [nota,         setNota]         = useState('');
  const [metodoPago,   setMetodoPago]   = useState('recibir');
  const [direccionEntrega, setDireccion] = useState('');
  const [instrucciones,    setInstruc]  = useState('');
  const [enviando,     setEnviando]     = useState(false);
  const [error,        setError]        = useState('');

  const items = carrito.items || [];
  const total = totalCarrito();

  useEffect(() => {
    if (!carrito.negocioId) { setNegocio(null); return; }
    setCargandoNeg(true);
    apiNegocios.detalle(carrito.negocioId)
      .then(setNegocio)
      .catch(() => setNegocio(null))
      .finally(() => setCargandoNeg(false));
  }, [carrito.negocioId]);

  const enviar = async () => {
    if (items.length === 0) return;
    setError('');
    setEnviando(true);
    try {
      const payload = {
        negocioId: parseInt(carrito.negocioId, 10),
        items: items.map(it => ({
          productoId: parseInt(it.productoId, 10),
          cantidad:   it.cantidad,
        })),
        metodoPago,
        nota:                nota || null,
        direccionEntrega:    direccionEntrega || null,
        instrucciones:       instrucciones || null,
      };
      await apiPedidos.crear(payload);
      vaciarCarrito();
      navigate('/veci/pedidos');
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo enviar el pedido. Verifica los datos.');
    } finally { setEnviando(false); }
  };

  if (!carrito.negocioId || items.length === 0) {
    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="overline" color="text.secondary">Carrito</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tu carrito está vacío</Typography>
        </Box>
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <StorefrontIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Elige un negocio del barrio y arma tu pedido.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/veci')}>Ver el directorio</Button>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  const aceptaFiado = negocio?.aceptaFiado ?? negocio?.AceptaFiado ?? true;
  const nombreNeg   = negocio?.nombre || negocio?.Nombre || 'Negocio';
  const direccionNeg = negocio?.direccion || negocio?.Direccion;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Carrito</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Tu pedido</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Pedido a</Typography>
              {cargandoNeg ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{nombreNeg}</Typography>
                  {direccionNeg && (
                    <Typography variant="body2" color="text.secondary">{direccionNeg}</Typography>
                  )}
                </>
              )}
            </Box>
            <Button size="small" color="inherit" onClick={vaciarCarrito}>Vaciar carrito</Button>
          </Box>

          <Divider sx={{ mb: 1 }} />

          {items.map((it) => (
            <Box
              key={it.productoId}
              sx={{
                py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 2, borderBottom: '1px solid', borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography fontWeight={500} noWrap>{it.nombre}</Typography>
                <Typography variant="body2" color="text.secondary">{formatoCOP(it.precio)} c/u</Typography>
              </Box>
              <Box sx={{
                display: 'flex', alignItems: 'center', border: '1px solid',
                borderColor: 'primary.main', borderRadius: 2, overflow: 'hidden',
              }}>
                <IconButton size="small" onClick={() => cambiarCantidadCarrito(it.productoId, it.cantidad - 1)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ px: 1.5, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>
                  {it.cantidad}
                </Typography>
                <IconButton size="small" onClick={() => cambiarCantidadCarrito(it.productoId, it.cantidad + 1)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontFamily: 'Fraunces', fontWeight: 600, minWidth: 90, textAlign: 'right' }}>
                {formatoCOP(it.precio * it.cantidad)}
              </Typography>
              <IconButton size="small" onClick={() => quitarDelCarrito(it.productoId)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Entrega */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontFamily: 'Fraunces' }}>Entrega</Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Dirección de entrega (opcional)"
              placeholder="Si la dejas vacía se usa la de tu perfil"
              value={direccionEntrega}
              onChange={(e) => setDireccion(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Instrucciones para el domiciliario"
              placeholder="Ej: torre B apto 502, portón verde…"
              value={instrucciones}
              onChange={(e) => setInstruc(e.target.value)}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Método de pago */}
      <Card>
        <CardContent>
          <FormControl>
            <FormLabel sx={{ fontFamily: 'Fraunces', color: 'text.primary', mb: 1 }}>Método de pago</FormLabel>
            <RadioGroup value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
              {METODOS_PAGO.map(m => {
                const desactivado = m.v === 'fiado_solicitud' && !aceptaFiado;
                return (
                  <FormControlLabel
                    key={m.v}
                    value={m.v}
                    disabled={desactivado}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{m.label}{desactivado ? ' (no disponible)' : ''}</Typography>
                        <Typography variant="caption" color="text.secondary">{m.descripcion}</Typography>
                      </Box>
                    }
                  />
                );
              })}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Nota */}
      <Card>
        <CardContent>
          <TextField
            fullWidth multiline minRows={2}
            placeholder="Nota para el negocio (opcional)"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </CardContent>
      </Card>

      <Alert severity="info" variant="outlined">
        En HolaVeci solo puedes tener un pedido a la vez con un mismo negocio. Si vas a otro negocio, este carrito se vaciará.
      </Alert>

      {/* Total y enviar */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Total del pedido</Typography>
              <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>{formatoCOP(total)}</Typography>
              <Chip label={METODOS_PAGO.find(m => m.v === metodoPago)?.label} variant="outlined" size="small" sx={{ mt: 0.5 }} />
            </Box>
            <Button
              variant="contained"
              size="large"
              endIcon={<SendIcon />}
              onClick={enviar}
              disabled={enviando}
            >
              {enviando ? 'Enviando…' : 'Enviar pedido'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}
